import { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Upload, message } from 'antd';
import PSAIModal from '@PSAIComponents/PSAIModal';
import API from '@api/api';
import Utils from '@utils/utils';
import styles from './index.module.scss';
import SampleExamples from './SampleExamples';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';
const MAX_COUNT = 20;
const ICON_URL = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/images_v3/uploadModelIcon.png';
const ACCEPT_MIME_SET = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const ACCEPT_EXT_SET = new Set(['jpg', 'jpeg', 'png', 'webp', 'avif']);

const removeExtension = (filename) => {
    if (filename === '.' || filename === '..') return filename;
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1 || lastDot === 0) return filename;
    return filename.substring(0, lastDot);
};

const isAcceptedImageFile = (file) => {
    if (!file) return false;
    const mimeType = (file.type || '').toLowerCase();
    if (ACCEPT_MIME_SET.has(mimeType)) return true;
    const name = file.name || '';
    const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
    return ACCEPT_EXT_SET.has(ext);
};

const getFileIdentity = (file) => {
    if (!file) return '';
    return `${file.webkitRelativePath || ''}|${file.name || ''}|${file.size || 0}|${file.lastModified || 0}`;
};

const getBatchIdentity = (files) => {
    const list = (Array.isArray(files) ? files : [])
        .map(getFileIdentity)
        .filter(Boolean)
        .sort();
    return list.join('||');
};

const readAllDirectoryEntries = async (directoryEntry) => {
    const reader = directoryEntry.createReader();
    const entries = [];
    let batch;
    do {
         batch = await new Promise((resolve, reject) => {
             reader.readEntries(resolve, reject);
         });
         if (batch?.length) entries.push(...batch);
     } while (batch?.length);
    return entries;
};

const walkFileSystemEntry = async (entry) => {
    if (!entry) return [];
    if (entry.isFile) {
        const file = await new Promise((resolve, reject) => {
            entry.file(resolve, reject);
        });
        return file ? [file] : [];
    }
    if (entry.isDirectory) {
        const entries = await readAllDirectoryEntries(entry);
        const nested = await Promise.all(entries.map(walkFileSystemEntry));
        return nested.flat();
    }
    if (entry._items) {
        const nested = await walkFileSystemEntry(entry._items);
        return nested;
    }
    if (entry._children && typeof entry._children === 'object') {
        const childEntries = Object.values(entry._children);
        const nested = await Promise.all(childEntries.map(walkFileSystemEntry));
        return nested.flat();
    }
    return [];
};

const collectFilesFromDataTransferItems = async (items) => {
    const entries = [];
    const handles = [];
    const fallbackFiles = [];
    for (const rawItem of items) {
        const item = rawItem;
        if (item?.getAsFileSystemHandle) {
            try {
                const handle = await item.getAsFileSystemHandle();
                if (handle) handles.push(handle);
            } catch (err) {
                console.warn('getAsFileSystemHandle 读取失败', err);
            }
        }
        const entry = item?.webkitGetAsEntry?.();
        if (entry) {
            entries.push(entry);
        } else {
            const file = item?.getAsFile?.();
            if (file) fallbackFiles.push(file);
        }
    }
    const walkHandle = async (handle) => {
        if (!handle) return [];
        if (handle.kind === 'file') {
            const file = await handle.getFile();
            return file ? [file] : [];
        }
        if (handle.kind === 'directory') {
            const childList = [];
            for await (const child of handle.values()) {
                childList.push(child);
            }
            const nested = await Promise.all(childList.map(walkHandle));
            return nested.flat();
        }
        return [];
    };
    const nestedByHandle = await Promise.all(handles.map(walkHandle));
    const nested = await Promise.all(entries.map(walkFileSystemEntry));
    return [...nestedByHandle.flat(), ...nested.flat(), ...fallbackFiles];
};

const MaterialUploadModal = ({ open, scope, folderId, onSuccess, onCancel, onUpload, resourceType, commoditySceneMode, sampleType = 'model' }) => {
    const [uploading, setUploading] = useState(false);
    const timerRef = useRef(null);
    const uploadingRef = useRef(false);
    const recentBatchRef = useRef({ key: '', ts: 0 });

    const uploadFileWithAntispamId = useCallback((file, figureCheck) => {
        return Utils.checkImageIslegal(file, 1, null, figureCheck).then(res => {
            if (!res) return false;
            if (!res.isLegal) {
                PSAIModal.confirm({
                    content: '经检测，您上传的内容涉嫌违规，已进行屏蔽处理，人工审核会对图片进行复核。',
                    okText: '知道了',
                    cancelText: '取消',
                    onOk() { },
                    onCancel() { },
                });
                return false;
            }
            const { imageAntispamId, data } = res;
            return { url: data, imageAntispamId };
        });
    }, []);

    const checkImgaeFile = useCallback(async (file, figureCheck) => {
        const name = removeExtension(file.name);

        // 1. 基础格式与大小校验（validateImageFile 内置了 30M 限制和格式校验）
        const isNromal = await Utils.validateImageFile(file, true);
        if (!isNromal) return false;

        // 2. 宽高不小于 300x300 校验
        const isSizeValid = await new Promise((resolve) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                if (img.width < 300 || img.height < 300) {
                    resolve(false);
                } else {
                    resolve(true);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(false);
            };
            img.src = objectUrl;
        });

        if (!isSizeValid) {
            throw new Error('图片宽高分辨率不小于300*300px');
        }

        const resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, 4096);
        if (!resizeRes || !resizeRes.success) throw new Error(resizeRes?.message);
        const resizedFile = resizeRes.file;
        const res = await uploadFileWithAntispamId(resizedFile, figureCheck);
        if (!res) return false;
        return { name, url: res.url, imageAntispamId: res.imageAntispamId, width: resizeRes.width, height: resizeRes.height };
    }, [uploadFileWithAntispamId]);

    const checkImageBasicValid = useCallback(async (file) => {
        const isNromal = await Utils.validateImageFile(file, true);
        if (!isNromal) return false;
        const isSizeValid = await new Promise((resolve) => {
            const objectUrl = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                if (img.width < 300 || img.height < 300) {
                    resolve(false);
                } else {
                    resolve(true);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(false);
            };
            img.src = objectUrl;
        });
        if (!isSizeValid) {
            throw new Error('图片宽高分辨率不小于300*300px');
        }
        return true;
    }, []);

    const doUpload = useCallback(async (files) => {
        const list = Array.isArray(files) ? files : [];
        if (list.length === 0) return;
        if (uploadingRef.current) return;

        const sliced = list.slice(0, MAX_COUNT);
        const batchKey = getBatchIdentity(sliced);
        const now = Date.now();
        if (batchKey && recentBatchRef.current.key === batchKey && (now - recentBatchRef.current.ts) < 1500) {
            return;
        }
        recentBatchRef.current = { key: batchKey, ts: now };

        if (list.length > MAX_COUNT) {
            message.warning(`单次最多可上传${MAX_COUNT}张图片，将仅上传前${MAX_COUNT}张`);
        }

        uploadingRef.current = true;
        setUploading(true);
        try {
            if (onUpload && resourceType) {
                let succNum = 0;
                let errNum = 0;
                for (const file of sliced) {
                    try {
                        const valid = await checkImageBasicValid(file);
                        if (!valid) continue;
                        const res = await onUpload(file, resourceType, commoditySceneMode);
                        if (!res) continue;
                        succNum++;
                    } catch (err) {
                        errNum++;
                        message.warning(err?.message || '图片处理失败，请更换图片后重试');
                    }
                }
                if (succNum > 0) {
                    message.success('上传成功');
                    onCancel?.();
                    onSuccess?.();
                } else if (errNum > 0) {
                    message.error('上传失败');
                }
            } else {
                const items = [];
                for (const file of sliced) {
                    try {
                        const res = await checkImgaeFile(file, true);
                        if (!res) continue;
                        items.push({
                            imageUrl: res.url,
                            name: res.name,
                            folderId: folderId || '',
                            scope: scope || 'personal',
                            attr: {},
                        });
                    } catch (err) {
                        message.warning(err?.message || '图片处理失败，请更换图片后重试');
                    }
                }

                if (items.length === 0) return;

                const res = await API.materialResBatchSave({ items });
                if (res?.code === 0) {
                    message.success('上传成功');
                    onCancel?.();
                    onSuccess?.({ uploadedUrls: items.map(item => item.imageUrl) });
                } else {
                    message.error(res?.message || '上传失败');
                }
            }
        } catch (err) {
            message.error(err?.message || '上传失败，请稍后重试');
        } finally {
            uploadingRef.current = false;
            setUploading(false);
        }
    }, [checkImageBasicValid, checkImgaeFile, commoditySceneMode, folderId, onCancel, onSuccess, onUpload, resourceType, scope]);

    const beforeUpload = useCallback((file, fileList) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            doUpload(fileList);
        }, 0);
        return false;
    }, [doUpload]);

    const handleDrop = useCallback(async (e) => {
        e.stopPropagation();
        const collectedFiles = [];
        try {
            if ("getFilesAndDirectories" in e.dataTransfer) {
                const filesAndDirs = await e.dataTransfer.getFilesAndDirectories();
                const fromDirs = await Utils.getFilesFromDirs(filesAndDirs, '/');
                if (Array.isArray(fromDirs) && fromDirs.length) {
                    collectedFiles.push(...fromDirs);
                } else if (filesAndDirs?.length) {
                    for (const entry of filesAndDirs) {
                        const files = await walkFileSystemEntry(entry);
                        if (files.length) collectedFiles.push(...files);
                    }
                }
            }
            if (e.dataTransfer?.items?.length) {
                const fromItems = await collectFilesFromDataTransferItems(e.dataTransfer.items);
                if (Array.isArray(fromItems) && fromItems.length) {
                    collectedFiles.push(...fromItems);
                }
            }
            if (e.dataTransfer?.files?.length) {
                const fromFiles = Array.from(e.dataTransfer.files);
                if (fromFiles.length) {
                    collectedFiles.push(...fromFiles);
                }
            }
        } catch (err) {
            console.error('拖拽解析失败', err);
        }
        const uniqueByPath = new Map();
        for (const file of collectedFiles) {
            if (!file) continue;
            const key = file.webkitRelativePath || `${file.name}_${file.size}_${file.lastModified}`;
            if (!uniqueByPath.has(key)) uniqueByPath.set(key, file);
        }
        const imageFiles = Array.from(uniqueByPath.values()).filter(isAcceptedImageFile);
        if (!imageFiles.length) {
            message.warning('未检测到可上传图片，请拖拽包含 JPG/PNG/WEBP/AVIF 的文件夹或图片');
            return;
        }
        doUpload(imageFiles);
    }, [doUpload]);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    return (
        <PSAIModal
            title="上传参考图"
            maxWidth={1100}
            open={open}
            footer={null}
            maskClosable={!uploading}
            onCancel={() => {
                if (uploading) {
                    message.warning('上传中，请稍后');
                    return;
                }
                onCancel?.();
            }}
        >
            <div className={styles.container}>
                <Upload.Dragger
                    className={styles.uploader}
                    accept={ACCEPT}
                    showUploadList={false}
                    multiple
                    beforeUpload={beforeUpload}
                    onDrop={handleDrop}
                    disabled={uploading}
                >
                    <div className={styles.uploaderContent}>
                        <img src={ICON_URL} alt="" />
                        <span>将图片拖拽至此区域或点击下方按钮上传图片</span>
                        <Button type="primary" className={styles.uploadBtn} loading={uploading}>
                            上传参考图
                        </Button>
                    </div>
                </Upload.Dragger>
                <div className={styles.tips}>
                    <p>图片上传说明：</p>
                    <p>支持JPG/PNG/WEBP/AVIF格式，图片小于等于30M，分辨率不小于300*300px，单次最多可上传20张</p>
                </div>
                <SampleExamples type={sampleType} />
            </div>
        </PSAIModal>
    );
};

export default MaterialUploadModal;
